import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MODULES, DEFAULT_WEIGHTS, DEFAULT_IC_THRESHOLD, score, type ModuleKey, type Deal } from '@krit/core';
import { listDeals, getSettings, patchSettings } from '../lib/api.js';
import { Card, StatTile, PageHeader, Pill } from '../components/ui.js';

export function Calibration() {
  const qc = useQueryClient();
  const { data: dealsData } = useQuery({ queryKey: ['deals'], queryFn: listDeals, retry: false });
  const { data: settingsData } = useQuery({ queryKey: ['settings'], queryFn: getSettings, retry: false });

  const savedWeights = (settingsData?.settings?.weights ?? DEFAULT_WEIGHTS) as Record<ModuleKey, number>;
  const savedThreshold = settingsData?.settings?.icThreshold ?? DEFAULT_IC_THRESHOLD;

  const [weights, setWeights] = useState<Record<ModuleKey, number> | null>(null);
  const [threshold, setThreshold] = useState<number | null>(null);
  const w = weights ?? savedWeights;
  const thr = threshold ?? savedThreshold;

  const deals = useMemo(() => dealsData?.deals ?? [], [dealsData]);
  const sum = MODULES.reduce((a, m) => a + (w[m.k] ?? m.w), 0);

  const impact = useMemo(
    () => deals
      .map((d) => ({ short: d.short, sector: d.sector, s: score(d as Deal, w), pass: score(d as Deal, w) >= thr }))
      .sort((a, b) => b.s - a.s),
    [deals, w, thr],
  );
  const passers = impact.filter((x) => x.pass).length;
  const dirty = MODULES.some((m) => w[m.k] !== savedWeights[m.k]) || thr !== savedThreshold;

  const save = useMutation({
    mutationFn: () => patchSettings({ weights: w, icThreshold: thr }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings'] }); qc.invalidateQueries({ queryKey: ['deals'] }); },
  });

  return (
    <div>
      <PageHeader eyebrow="Tools" title="Score Calibration" desc="Tune module weights and the IC pass threshold — the impact preview updates live." />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 max-w-[680px]">
        <StatTile label="Weights sum" value={`${sum}%`} note={sum === 100 ? 'balanced' : 'need not total 100'} accent={sum === 100 ? 'green' : 'amber'} />
        <StatTile label="IC threshold" value={thr} note="min Krit Score" accent="navy" />
        <StatTile label="Qualify" value={`${passers}/${deals.length}`} note="pipeline deals" accent="teal" />
        <StatTile label="Avg score" value={deals.length ? Math.round(impact.reduce((a, x) => a + x.s, 0) / deals.length) : 0} note="pipeline" accent="purple" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 items-start">
        <Card title="Module weights" sub={`Weighted average across ${MODULES.length} modules`}
          actions={<div className="flex gap-2">
            <button onClick={() => { setWeights({ ...DEFAULT_WEIGHTS }); setThreshold(DEFAULT_IC_THRESHOLD); }} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-white text-navy border border-line hover:bg-canvas">Reset</button>
            <button disabled={!dirty || save.isPending} onClick={() => save.mutate()} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-teal text-white disabled:opacity-50">{save.isPending ? 'Saving…' : 'Save'}</button>
          </div>}>
          {MODULES.map((m) => (
            <div key={m.k} className="mb-3.5 last:mb-0">
              <div className="flex justify-between text-[12.5px] mb-1.5"><span className="font-semibold text-navy">{m.n}</span><span className="mono font-bold text-navy">{w[m.k] ?? m.w}%</span></div>
              <input type="range" min={0} max={40} value={w[m.k] ?? m.w} onChange={(e) => setWeights({ ...w, [m.k]: Number(e.target.value) })} className="w-full accent-teal" />
            </div>
          ))}
          <div className="mt-5 pt-4 border-t border-line-2">
            <label className="block text-[11px] font-semibold text-muted mb-1.5">IC pass threshold (min Krit Score)</label>
            <input type="number" value={thr} onChange={(e) => setThreshold(Number(e.target.value))} className="w-32 px-3 py-2 border border-line rounded-lg text-[13px] mono" />
            <div className="mt-3 text-[12.5px] bg-canvas border border-line rounded-lg px-3.5 py-2.5">
              At a threshold of <b className="text-navy">{thr}</b>, <b className="text-navy">{passers}</b> of {deals.length} pipeline deals qualify.
            </div>
            {dirty && <p className="text-[11px] text-amber mt-2">Unsaved changes.</p>}
          </div>
        </Card>

        <Card title="Impact preview" sub="Score vs threshold, live" bodyClass="p-0">
          <div className="max-h-[520px] overflow-y-auto divide-y divide-line-2">
            {impact.map((x) => (
              <div key={x.short} className="flex justify-between items-center px-4 py-2.5 text-[12.5px]">
                <span className="min-w-0"><b className="text-navy">{x.short}</b> <span className="text-muted-2">{x.sector}</span></span>
                <span className="flex items-center gap-2 flex-none"><span className="mono font-bold text-navy">{x.s}</span><Pill tone={x.pass ? 'green' : 'red'}>{x.pass ? 'clears' : 'below'}</Pill></span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
